/**
 * waap-analytics-board.tsx — Shared shell for every WAAP analytics sub-board.
 *
 * Renders a toolbar (Time Range select + optional Domain multi-select) then
 * delegates to WaapChartGrid for the four-lane chart layout. State (range,
 * domains) is owned by the parent board component in waap-analytics-tab.tsx.
 *
 * Mirrors Partner's WaapAnalyticsBoard.tsx — ops delta: uses actAsClient +
 * anchorId prop instead of WaapWebsiteProvider context; no CSS modules.
 */

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import {
  Button,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  MultiSelect,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { EmptyState } from '@vnetwork/ui/data';
import type { WaapChartsResponse } from '@/api/act-as-waap';
import { useActAsWAAPWebsites } from '@/api/act-as-waap';
import { WaapChartGrid } from './waap-chart-widgets';

/* ── Board query shape ───────────────────────────────────────────────────── */

export interface WaapBoardQuery {
  data: WaapChartsResponse | null | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** Optional — toolbar Refresh button is hidden when absent. */
  refetch?: () => void;
  isFetching?: boolean;
}

/* ── Props ───────────────────────────────────────────────────────────────── */

export interface WaapAnalyticsBoardProps<R extends string> {
  icon: ReactNode;
  label: string;
  ranges: readonly R[];
  range: R;
  onRangeChange: (r: R) => void;
  /** Omit for boards without a Domain filter (e.g. API board). */
  domains?: string[];
  onDomainsChange?: (d: string[]) => void;
  /** Anchor domain UUID — used to load the website list for the domain picker. */
  anchorId: string;
  query: WaapBoardQuery;
}

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * Shared shell for all simple analytics boards.
 * Toolbar → loading/error/not-configured states → four-lane chart grid.
 */
export function WaapAnalyticsBoard<R extends string>({
  icon,
  label,
  ranges,
  range,
  onRangeChange,
  domains,
  onDomainsChange,
  anchorId,
  query,
}: WaapAnalyticsBoardProps<R>) {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch, isFetching } = query;

  // Website list drives the optional domain multi-select.
  const websitesQ = useActAsWAAPWebsites(anchorId, domains !== undefined);
  const websiteOptions = (websitesQ.data ?? []).map((w) => ({ value: w.domain, label: w.domain }));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

      {/* ── Toolbar ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-3)', alignItems: 'center' }}>

        {domains !== undefined && onDomainsChange && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', whiteSpace: 'nowrap' }}>
              {t('waap_domain.analytics_filter_domain', 'Domain')}
            </span>
            <MultiSelect
              placeholder={t('waap_domain.analytics_all_domains', 'All domains')}
              options={websiteOptions}
              value={domains}
              onChange={onDomainsChange}
              emptyHint={t('waap_domain.analytics_no_websites', 'No websites available')}
            />
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', whiteSpace: 'nowrap' }}>
            {t('waap_domain.analytics_filter_range', 'Time Range')}
          </span>
          <Select value={range} onValueChange={(v) => onRangeChange(v as R)}>
            <SelectTrigger aria-label={t('waap_domain.analytics_filter_range', 'Time Range')} style={{ minWidth: '140px' }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ranges.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {refetch && (
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw size={14} style={{ marginRight: 'var(--vn-space-1)' }} />
            {t('waap_domain.refresh', 'Refresh')}
          </Button>
        )}
      </div>

      {/* ── Board content ─────────────────────────────────────────────── */}
      {isLoading ? (
        <Skeleton height={480} />
      ) : data === null ? (
        <EmptyState
          icon={icon}
          title={t('waap_domain.analytics_not_configured_title', '{{label}} analytics not configured', { label })}
          description={t('waap_domain.analytics_not_configured_desc', 'This board is not set up for this environment yet.')}
        />
      ) : isError ? (
        <Alert
          tone="danger"
          title={t('waap_domain.analytics_load_error', "Couldn't load {{label}} analytics", { label })}
        >
          {String((query.error as { message?: string })?.message ?? '')}
        </Alert>
      ) : (
        <WaapChartGrid charts={data?.charts ?? []} />
      )}
    </div>
  );
}
